import {isInteger, isFinite, isBoolean} from "lodash";
import moment from "moment";
import * as Yup from "yup";
import {ComparisonMethodType, IMonitorConfiguration} from "../api/monitorsModels";
import dataFormatUtil, {DataType} from "@/util/dataFormatUtil";
import monitorDataUtil from "@/util/monitorDataUtil";
import {MetricCode} from "@/features/metricDetails/api/metricDetailsModels";
export const validationSchema = (
    formAction: IMonitorConfiguration["formAction"]
): Yup.ObjectSchema<any> => {
    const validationSchema: Record<string, any> = {
        type: Yup.string().required("Required"),
        metricCriteria: Yup.array().of(
            Yup.object({
                metric: Yup.string()
                    .required("Required")
                    .test(
                        "notUnknown",
                        "Required",
                        (val) => val !== MetricCode.Unknown
                    ),
                measure: Yup.string().required("Required"),
                amount: Yup.string()
                    .required("Required")
                    .nullable() //nullable so that when it gets wiped out when changing type, the validation error does not show
                    //"" is specified as the error message simply as a placeholder.  Since the message changes based on the type of metric selected
                    //and since they can be multiple metric criteria the message is dynamically created via createError so each message
                    //has the correct error based on the metric selected.
                    .test("validateDataType", "", function (value) {
                        const {path, createError} = this;

                        const selectedMetric =
                            monitorDataUtil.getColumnDefinitionFromMetricKey(
                                this.parent.metric
                            );

                        const unformattedValue =
                            dataFormatUtil.metricDataDisplayValueToRawData(
                                value,
                                selectedMetric.dataType
                            );

                        const isValid = validateDataType(
                            unformattedValue,
                            selectedMetric.dataType,
                            selectedMetric.decimalPlaces
                        );

                        if (!isValid) {
                            const validationErrorMessage =
                                getAmountValidationErrorMessage(
                                    selectedMetric.dataType
                                );

                            return createError({
                                path,
                                message: validationErrorMessage
                            });
                        }

                        return true;
                    })
            })
        ),
        tenderType: Yup.array().of(Yup.string()).required("Required"),
        hourOfDayStart: Yup.number().required("Required"),
        hourOfDayEnd: Yup.number()
            .required("Required")
            .moreThan(
                Yup.ref("hourOfDayStart"),
                "End Time must be after Start Time"
            ),
        orderChannel: Yup.array()
            .of(Yup.string())
            .required("Required")
            .test(
                "non-empty",
                "The array cannot be empty",
                (value) => value && value.length > 0
            )
    };

    // Set name to be required in the validation schema if we are not on the search page (search page doesn't require a monitor name)
    if (formAction !== "SEARCH") {
        validationSchema["name"] = Yup.string()
            .required("Required")
            .max(256, "Name must be less than 256 characters");
    }
    // Set businessUnitId to be required in the validation shcema if user is searching
    if (formAction === "SEARCH") {
        validationSchema["businessUnitId"] = Yup.string()
            .required("Required")
            .length(36, "BusinessUnitId must be 36 characters");
    }

    // Set Start and End to be required in the validation schema if we are on the search page
    if (formAction === "SEARCH") {
        validationSchema["searchStartDate"] = Yup.date()
            .required("Required")
            .min(
                moment().add(-13, "months").toDate(),
                "Date must be within last 13 months"
            );
        validationSchema["searchEndDate"] = Yup.date()
            .required("Required")
            .min(
                moment().add(-13, "months").toDate(),
                "Date must be within last 13 months"
            )
            .when(
                "searchStartDate",
                ([searchStartDate]: Date[], schema) =>
                    searchStartDate &&
                    schema
                        .max(
                            moment(searchStartDate).add(28, "days").toDate(),
                            "Maximum date range is 28 days"
                        )
                        .min(
                            moment(searchStartDate).toDate(),
                            "Start Date must be before End Date"
                        )
            );
    }

    return Yup.object(validationSchema);
};

export function getAmountValidationErrorMessage(
    dataType: DataType | undefined,
    comparisonType: ComparisonMethodType | undefined = undefined,
    value1?: number,
    value2?: number
): string {
    let errorMessage = "";
    switch (dataType) {
        // TODO: Implement missing DataTypes, i.e. Date
        case DataType.Money:
            errorMessage = "Enter a Dollar Amount, e.g. 5.24 or $5.24";
            break;

        case DataType.Percent:
            errorMessage = "Enter a Percentage, e.g. 12.3 or 12.2%";
            break;

        case DataType.TimeSpan:
            errorMessage = "Enter Time in Minutes, e.g. 4:15 or 4.25";
            break;
        case DataType.PrettyTimeSpan:
             
            errorMessage = 'Enter the time and unit, e.g. "1d", "1h", or "1m 45s"';
            break;
        case DataType.Boolean:
            errorMessage = "Select True or False";
            break;
        case DataType.Time:
        case DataType.TimeOfDay:
            if (comparisonType === ComparisonMethodType.Between
                && value1 !== undefined
                && value2 !== undefined
                && value1 >= value2) {
                errorMessage = "The end time must be after the start time";
            }
            else {
                errorMessage = "Enter a Time of Day, e.g. 4:15 AM or 12:30 PM";
            }
            break;
        default:
            errorMessage = "Enter a Number, e.g. 35";
            break;
    }

    return errorMessage;
}

export function validateDataType(
    formattedValue: number | boolean | null | undefined,
    dataType: DataType | undefined,
    decimalPlaces: number | undefined
): boolean {
    //using lodash isInteger and isFinite for IE support
    switch (dataType) {
        case DataType.Number:
            return decimalPlaces !== undefined && decimalPlaces > 0
                ? isFinite(formattedValue)
                : isInteger(formattedValue);
        case DataType.Boolean:
            return isBoolean(formattedValue);
        case DataType.TimeOfDay:
        case DataType.Time:
            return (
                isInteger(formattedValue) &&
                typeof formattedValue === "number" &&
                formattedValue < 1440 // number of minutes in a day
            );
        default:
            return isFinite(formattedValue);
    }
}
